import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { NotificationsService } from '@geekbears/ngx-gb-ui-kit';
import {
  getAllUsersFailureAction,
  getAllUsersSuccessAction,
  UsersActions,
  allUsersLoadingSelector,
  usersCountSuccessSelector,
} from '@modules/company-manager/users/application';
import { AppUser } from '@modules/company-manager/users/domain';
import { Company } from '@modules/super-admin/company/domain';
import {
  ClientsActions,
  addMultipleMembersFailureAction,
  addMultipleMembersLoadingSelector,
  addMultipleMembersSuccessAction,
} from '@modules/company-manager/clients/application';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Roles } from '@utils/roles';
import { isNil } from 'lodash';
import * as moment from 'moment';
import { filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'app-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
})
export class UsersListComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * @destroy$ is used to unsubscribe from all subscriptions
   */
  destroy$: Subject<void> = new Subject();
  detailsSelected: string = '';
  currentPage: number = 1;
  itemsPerPages: number = 6;

  usersComplete!: any[];
  users!: any[];
  usersLoading$: Observable<boolean | undefined>;
  lastUpdated!: any;

  usersFlag$!: Observable<any>;
  currentCompany!: Company;
  usersCount$: Observable<any>;
  currentCompanyId: string = '';
  addMultipleMembersLoading$: Observable<boolean | undefined>;
  isInviteLoading: boolean = false;

  constructor(
    private store$: Store,
    private actions$: Actions,
    private notificationsService: NotificationsService,
    private router: Router
  ) {
    this.usersLoading$ = this.store$.select(allUsersLoadingSelector);
    this.usersCount$ = this.store$.select(usersCountSuccessSelector);
    this.addMultipleMembersLoading$ = this.store$.select(
      addMultipleMembersLoadingSelector
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCompanyId() {
    let currentUser = JSON.parse(localStorage.getItem('s_user') || '{}');
    if (currentUser) {
      this.currentCompanyId = currentUser._companyId || '';
      this.getAllUsers(currentUser._companyId);
      this.getUsersCount(currentUser._companyId);
    }
  }

  ngAfterViewInit(): void {}
  getAllUsers(companyId: string) {
    this.store$.dispatch(
      UsersActions.getAllUsers({
        companyId: companyId,
      })
    );
  }

  ngOnInit(): void {
    this.getCompanyId();
    this.store$.dispatch(
      UsersActions.switchToAdmin({
        status: true,
      })
    );
    this.onFailureActions();
    this.onResendInviteActions();
    this.addMultipleMembersLoading$.pipe(
      tap((loading) => {
        this.isInviteLoading = !!loading;
      }),
      takeUntil(this.destroy$)
    ).subscribe();
    this.usersFlag$ = this.actions$.pipe(
      ofType(getAllUsersSuccessAction),
      filter((action) => {
        return !isNil(action.users);
      }),
      map((action: any) => {
        if (action) {
          this.users = action.users.data;
          this.usersComplete = action.users.data;
          return action.users.data;
        }
      }),
      tap((users: any[]) => {
        if (users.length > 0) {
          this.lastUpdated = moment(users[users.length - 1].createdAt).format(
            'MM/DD/YYYY'
          );
          this.currentCompany = this.users[0].company.companyName;
        }
      })
    );
  }

  userType(roles: string[]) {
    if (roles.find((role) => role == Roles.companyAdministrator)) {
      return 'Company Administrator';
    }
    if (roles.find((role) => role == 'approver')) {
      return 'Approver';
    }
    if (roles.find((role) => role == 'requester')) {
      return 'Requester';
    }
    return '';
  }

  pageChanged(event: any) {
    this.currentPage = event;
  }

  filterChange(event: any) {
    this.users = this.usersComplete.filter((client) =>
      JSON.stringify(client)
        .toLocaleLowerCase()
        .includes(event.toLocaleLowerCase())
    );
  }

  getUsersCount(companyId: string) {
    this.store$.dispatch(
      UsersActions.getUsersCount({
        companyId: companyId,
      })
    );
  }
  onFailureActions() {
    this.actions$
      .pipe(
        ofType(getAllUsersFailureAction),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          if (errorMessage) {
            this.notificationsService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  userDetails(user: AppUser) {
    this.router.navigateByUrl(`dashboard/users/user-details/${user.id}`);
  }

  getUserRole(roles: string[]): string {
    if (roles.includes('approver')) {
      return 'approver';
    }
    if (roles.includes('requester')) {
      return 'requester';
    }
    return roles.includes(Roles.companyAdministrator) ? Roles.companyAdministrator : (roles[0] || 'requester');
  }

  resendInvite(user: any) {
    const role = this.getUserRole(user.roles);
    const companyId = user._companyId || this.currentCompanyId;
    this.store$.dispatch(
      ClientsActions.addMultipleMembers({
        members: [
          {
            email: user.email,
            role: role,
            _companyId: companyId,
          } as any,
        ],
      })
    );
  }

  onResendInviteActions() {
    this.actions$
      .pipe(
        ofType(addMultipleMembersSuccessAction),
        filter((action) => !isNil(action.response)),
        tap((action: any) => {
          if (action.response.data && action.response.message) {
            this.notificationsService.error('There are already users using some emails from the list', {
              duration: 3000,
            });
          } else {
            this.notificationsService.success('Invite resent successfully', {
              duration: 3000,
              icon: 'check-circle',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(addMultipleMembersFailureAction),
        map((action) => action.failure),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          if (errorMessage) {
            this.notificationsService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
}
